import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface ExtractedOperation {
    description: string;
    amount: number;
    type: 'REVENUE' | 'EXPENSE';
    currency: string;
    date: string;
    category?: string;
    confidence: number;
    metadata?: Record<string, any>;
    savedId?: string;
}

export interface AnalysisSummary {
    totalOperations: number;
    totalRevenue: number;
    totalExpense: number;
    detectedCurrencies: string[];
}

export interface AnalysisMetadata {
    provider: string;
    processingTime: number;
    documentType: string;
    originalName?: string;
    fileSize?: number;
    mimeType?: string;
    backend?: string;
}

export interface DocumentAnalysisResult {
    success: boolean;
    operations: ExtractedOperation[];
    summary: AnalysisSummary;
    metadata: AnalysisMetadata;
    warnings?: string[];
    // When saved to journal
    saved?: boolean;
    savedCount?: number;
    savedOperations?: ExtractedOperation[];
    failedOperations?: ExtractedOperation[];
    failedCount?: number;
}

export interface SupportedFileType {
    mimeType: string;
    extension: string;
}

export interface SupportedFileTypesResponse {
    maxFileSize: number;
    maxFileSizeMB: number;
    categories: {
        image?: SupportedFileType[];
        pdf?: SupportedFileType[];
        spreadsheet?: SupportedFileType[];
        text?: SupportedFileType[];
    };
    mimeTypes: string[];
}

export interface ProviderHealth {
    provider: string;
    available: boolean;
    supportedCategories: string[];
    timestamp: string;
    error?: string;
}

export interface HealthStatusResponse {
    timestamp: string;
    providers: Record<string, ProviderHealth>;
    supportedCategories: string[];
}

// ============================================================================
// API Service
// ============================================================================

export const documentAnalysisService = {
    /**
     * Analyze a document (preview only, don't save)
     */
    async analyze(file: File, options?: { currency?: string }): Promise<DocumentAnalysisResult> {
        const formData = new FormData();
        formData.append('file', file);

        if (options?.currency) {
            formData.append('currency', options.currency);
        }

        const response = await apiClient.post('/documents/analyze', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 60000, // 60 second timeout for large files
        });

        return response.data.data;
    },

    /**
     * Analyze a document and save operations to a journal
     */
    async analyzeAndSave(
        file: File,
        journalId: string,
        options?: { currency?: string }
    ): Promise<DocumentAnalysisResult> {
        const formData = new FormData();
        formData.append('file', file);

        if (options?.currency) {
            formData.append('currency', options.currency);
        }

        const response = await apiClient.post(`/documents/analyze/${journalId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 60000,
        });

        return response.data.data;
    },

    /**
     * Get supported file types
     */
    async getSupportedFileTypes(): Promise<SupportedFileTypesResponse> {
        const response = await apiClient.get('/documents/supported-types');
        return response.data.data;
    },

    /**
     * Get provider health status
     */
    async getHealthStatus(): Promise<HealthStatusResponse> {
        const response = await apiClient.get('/documents/health');
        return response.data.data;
    },
};

// ============================================================================
// Error Code Helpers
// ============================================================================

/**
 * Document analysis error codes and their user-friendly messages
 * These map to translation keys
 */
export const DOCUMENT_ANALYSIS_ERROR_CODES: Record<string, string> = {
    // File validation errors
    DA_001: 'documentAnalysis.errors.noFileProvided',
    DA_002: 'documentAnalysis.errors.fileTooLarge',
    DA_003: 'documentAnalysis.errors.unsupportedFileType',
    DA_004: 'documentAnalysis.errors.fileCorrupted',
    DA_005: 'documentAnalysis.errors.fileEmpty',
    DA_006: 'documentAnalysis.errors.fileReadError',

    // Provider errors
    DA_100: 'documentAnalysis.errors.providerNotConfigured',
    DA_101: 'documentAnalysis.errors.providerNotFound',
    DA_102: 'documentAnalysis.errors.providerConnectionFailed',
    DA_103: 'documentAnalysis.errors.providerAuthFailed',
    DA_104: 'documentAnalysis.errors.providerTimeout',
    DA_105: 'documentAnalysis.errors.providerUnavailable',

    // Parsing errors
    DA_200: 'documentAnalysis.errors.analysisFailed',
    DA_201: 'documentAnalysis.errors.noOperationsFound',
    DA_202: 'documentAnalysis.errors.invalidDataFormat',
    DA_203: 'documentAnalysis.errors.partialExtraction',
    DA_204: 'documentAnalysis.errors.ambiguousCurrency',
    DA_205: 'documentAnalysis.errors.dateParsingFailed',
    DA_206: 'documentAnalysis.errors.amountParsingFailed',

    // Rate limiting
    DA_300: 'documentAnalysis.errors.rateLimitExceeded',
    DA_301: 'documentAnalysis.errors.quotaExceeded',
    DA_302: 'documentAnalysis.errors.concurrentLimit',
};

/**
 * Get the translation key for a document analysis error code
 */
export function getDocumentAnalysisErrorKey(code: string): string {
    return DOCUMENT_ANALYSIS_ERROR_CODES[code] || 'documentAnalysis.errors.generic';
}

/**
 * Check if an error is a document analysis error
 */
export function isDocumentAnalysisError(error: any): boolean {
    const code = error?.response?.data?.code || error?.code;
    return typeof code === 'string' && code.startsWith('DA_');
}

/**
 * Extract the error code from an error object
 */
export function getErrorCode(error: any): string | null {
    return error?.response?.data?.code || error?.code || null;
}
